import { computed, inject } from '@angular/core';
import { DISCOVERY_ENVIRONMENT } from '../core/discovery-environment.token';
import { discoveryAuthority } from './discovery-authority';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { filter, firstValueFrom, Subscription, take, timeout } from 'rxjs';
import {
  OPEN_STATUSES,
  problemKeyOf,
  problemNameOf,
  type DiscoveryAnswer,
  type DiscoveryAssignment,
  type DiscoveryProblem,
} from './discovery-assignment.model';
import { DiscoveryAssignmentApiService } from './discovery-assignment-api.service';
import { DiscoveryAssignmentFirestoreService } from './discovery-assignment-firestore.service';

interface DiscoveryAssignmentState {
  assignments: DiscoveryAssignment[];
  selectedId: string | null;
  problemKey: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: DiscoveryAssignmentState = {
  assignments: [],
  selectedId: null,
  problemKey: null,
  loading: false,
  saving: false,
  error: null,
};

export const DiscoveryAssignmentStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    selected: computed(
      () => store.assignments().find((item) => item.id === store.selectedId()) ?? null,
    ),
    problems: computed<DiscoveryProblem[]>(() => {
      const grouped = new Map<string, DiscoveryProblem>();
      store.assignments().forEach((assignment) => {
        const key = problemKeyOf(assignment);
        let problem = grouped.get(key);
        if (!problem) {
          problem = {
            key,
            repository: assignment.repository,
            sourcePath: assignment.sourcePath,
            name: problemNameOf(assignment.sourcePath),
            sourceExcerpt: assignment.sourceExcerpt,
            sourceCommitSha: assignment.sourceCommitSha,
            assignments: [],
            openCount: 0,
          };
          grouped.set(key, problem);
        }
        problem.assignments.push(assignment);
        if (OPEN_STATUSES.includes(assignment.status)) problem.openCount += 1;
      });
      return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name));
    }),
  })),
  withComputed((store, environment = inject(DISCOVERY_ENVIRONMENT)) => ({
    authority: computed(() => discoveryAuthority(store.selected(), environment)),
    activeProblem: computed(
      () => store.problems().find((problem) => problem.key === store.problemKey()) ?? null,
    ),
    visibleAssignments: computed(() => {
      const key = store.problemKey();
      return key
        ? store.assignments().filter((assignment) => problemKeyOf(assignment) === key)
        : store.assignments();
    }),
  })),
  withMethods(
    (
      store,
      firestore = inject(DiscoveryAssignmentFirestoreService),
      api = inject(DiscoveryAssignmentApiService),
      environment = inject(DISCOVERY_ENVIRONMENT),
    ) => {
      let inboxSubscription: Subscription | null = null;
      const legacy = (assignment: DiscoveryAssignment) => {
        if (!discoveryAuthority(assignment, environment).canMutate) {
          patchState(store, {
            error:
              'This historical record is read-only. Its decision provenance does not permit a new mutation.',
          });
          return false;
        }
        if (assignment.visualizerContractJson == null && assignment.contractHash == null)
          return true;
        patchState(store, { error: 'Use the final decision card for this visualizer assignment.' });
        return false;
      };

      const awaitProjection = async (
        id: string,
        revision: number,
        status?: string,
      ): Promise<void> => {
        await firstValueFrom(
          firestore.watchOne(id).pipe(
            filter(
              (assignment): assignment is DiscoveryAssignment =>
                !!assignment &&
                assignment.responseRevision >= revision &&
                (!status || assignment.status === status),
            ),
            take(1),
            timeout(15_000),
          ),
        );
      };

      const run = async (command: () => Promise<void>): Promise<void> => {
        patchState(store, { saving: true, error: null });
        try {
          await command();
        } catch (error) {
          patchState(store, { error: error instanceof Error ? error.message : String(error) });
        } finally {
          patchState(store, { saving: false });
        }
      };

      return {
        load(identity: string): void {
          inboxSubscription?.unsubscribe();
          patchState(store, { loading: true, error: null });
          const subscription = firestore.watchInbox(identity).subscribe({
            next: (assignments) =>
              patchState(store, (current) => {
                const scoped = current.problemKey
                  ? assignments.filter((item) => problemKeyOf(item) === current.problemKey)
                  : assignments;
                return {
                  assignments,
                  selectedId:
                    current.selectedId && scoped.some((item) => item.id === current.selectedId)
                      ? current.selectedId
                      : (scoped[0]?.id ?? null),
                  loading: false,
                };
              }),
            error: (error) => patchState(store, { loading: false, error: String(error) }),
          });
          inboxSubscription = subscription;
        },
        select(id: string): void {
          patchState(store, { selectedId: id, error: null });
        },
        /** Scopes the view to one problem. A null key restores the full inbox. */
        selectProblem(key: string | null): void {
          patchState(store, (current) => {
            const scoped = key
              ? current.assignments.filter((item) => problemKeyOf(item) === key)
              : current.assignments;
            return {
              problemKey: key,
              selectedId:
                current.selectedId && scoped.some((item) => item.id === current.selectedId)
                  ? current.selectedId
                  : (scoped[0]?.id ?? null),
              error: null,
            };
          });
        },
        save(assignment: DiscoveryAssignment, answers: DiscoveryAnswer[]): Promise<void> {
          if (!legacy(assignment)) return Promise.resolve();
          return run(async () => {
            const response = await api.save(assignment.id, answers);
            await awaitProjection(assignment.id, Number(response.responseRevision));
          });
        },
        saveAndSubmit(assignment: DiscoveryAssignment, answers: DiscoveryAnswer[]): Promise<void> {
          if (!legacy(assignment)) return Promise.resolve();
          return run(async () => {
            const saved = await api.save(assignment.id, answers);
            const revision = Number(saved.responseRevision);
            await awaitProjection(assignment.id, revision, 'in_progress');
            const submitted = await api.submit(assignment.id, revision);
            await awaitProjection(assignment.id, Number(submitted.responseRevision), 'submitted');
          });
        },
        requestInput(assignment: DiscoveryAssignment, reason: string): Promise<void> {
          if (!legacy(assignment)) return Promise.resolve();
          return run(async () => {
            const response = await api.requestInput(assignment.id, reason);
            await awaitProjection(
              assignment.id,
              Number(response.responseRevision),
              'input_required',
            );
          });
        },
        review(
          assignment: DiscoveryAssignment,
          decision: 'accept' | 'request_changes' | 'reject',
          reason: string,
        ): Promise<void> {
          if (!legacy(assignment)) return Promise.resolve();
          return run(async () => {
            const response = await api.review(
              assignment.id,
              assignment.responseRevision,
              decision,
              reason,
            );
            const status =
              decision === 'accept'
                ? 'response_accepted'
                : decision === 'reject'
                  ? 'rejected'
                  : 'changes_requested';
            await awaitProjection(assignment.id, Number(response.responseRevision), status);
          });
        },
        destroy(): void {
          inboxSubscription?.unsubscribe();
        },
      };
    },
  ),
  withHooks({ onDestroy: (store) => store.destroy() }),
);
